import {
  MySupportInfoControllerService,
  PaymentService,
} from '@/__generated__/FrontApi';
import naviPrev from '@/assets/img/icons/arrow_left_circle@2x.png';
import naviNext from '@/assets/img/icons/arrow_right_circle@2x.png';
import Button from '@/components/Button';
import InputCheckbox from '@/components/Input/InputCheckbox';
import { H4 } from '@/components/Titles';
import { fakeCardMask, maskNumber } from '@/helpers/AddMaskingNumbers';
import { breakpoint } from '@/helpers/BreakpointHelper';
import {
  SALESFORCE_PAYMENT_METHOD_TYPE_CMS,
  SALESFORCE_PAYMENT_METHOD_TYPE_CARD,
  SALESFORCE_PAYMENT_METHOD_TYPE_NPAY,
  getPaymentTypeNameToCode,
  getPaymentTypeLabel,
  PAYTYPE_PAYMENT_DAY_LIST,
} from '@/helpers/PaymentHelper';
import { PaymentMethod, MyPymentMethodVo } from '@/helpers/TypesHelper';
import { InputRadioPledgePaymentDay } from '@/page-blocks/donate/DonateCommon';
import React, {
  useState,
  forwardRef,
  InputHTMLAttributes,
  useEffect,
  useCallback,
} from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation } from 'swiper';
import 'swiper/components/navigation/navigation.min.css';
import 'swiper/components/pagination/pagination.min.css';
import 'swiper/components/scrollbar/scrollbar.min.css';
import 'swiper/swiper.min.css';
import { useForm } from 'react-hook-form';
import Tooltip from '@/components/Tooltip';

// Swiper 모듈 설치
SwiperCore.use([Navigation]);

const RadioWrapper = styled.div`
  margin-top: 24px;
`;
const HiddenRadio = styled.input.attrs({ type: 'radio' })`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 2;

  &:disabled {
    cursor: default;
    pointer-events: none;
  }
`;
const CardInfo = styled.div`
  font-size: 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 3px;
  pointer-events: none;
  position: relative;
  z-index: 1;
  & > div {
    width: 100%;
    display: flex;
    align-items: center;
  }
`;
const StatusBadge = styled.span<{ variant: 'current' | 'currentUnselected' | 'available' | 'processing' | 'rejected' }>`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 2.18;
  pointer-events: none;
  z-index: 1;
  white-space: nowrap;

  ${({ variant }) => {
    switch (variant) {
      case 'current':
        return `background: #fff; color: #374EA2;`;
      case 'currentUnselected':
        return `background: rgba(226, 240, 246, 0.5); color: #374EA2;`;
      case 'available':
        return `background: rgba(226, 240, 246, 0.5); color: #374EA2;`;
      case 'processing':
        return `background: #E5E6E8; color: #2D2926;`;
      case 'rejected':
        return `background: #FBE3E2; color: #D4413A;`;
    }
  }}
`;
const RejectedMessage = styled.p`
  margin-top: 6px;
  font-size: 12px;
  color: #D4413A;
  opacity: 0.8;
  pointer-events: none;
  position: relative;
  z-index: 1;
`;
const RadioTabButton = styled.div<{
  checked: boolean;
  Npay: boolean;
  compact?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${({ compact }) => (compact ? 'auto' : '116px')};
  padding: ${({ compact }) => (compact ? '16px' : '20px')};
  border-radius: ${({ compact }) => (compact ? '12px' : '10px')};
  border: 1px solid ${({ checked }) => (checked ? '#1cabe2' : '#e5e6e8')};
  background: ${({ checked }) => (checked ? '#1cabe2' : 'transparent')};
  color: ${({ checked }) => (checked ? '#fff' : 'inherit')};
  transition: all 0.2s;
  overflow: hidden;
  cursor: pointer;

  &.disabled {
    cursor: default;
    background: #f8f9fa;
    color: rgba(45, 41, 38, 0.5);
  }
  &.processing-legacy {
    cursor: default;
    border: 1px solid #1cabe2;
    background: #1cabe2;
    color: #fff;
    opacity: 0.5;

    dt {
      color: #fff;
      opacity: 0.8;
    }
  }
  &.processing {
    cursor: default;
    border: 1px solid #E5E6E8;
    background: #F8F9FA;
    color: #2D2926;

    dt {
      color: #828385;
      opacity: 0.8;
    }
  }
  &.rejected {
    cursor: default;
    border: 1px solid #FBE3E2;
    background: #FEF8F8;
    color: #2D2926;

    dt {
      color: #828385;
      opacity: 0.8;
    }
  }
  &:hover {
    &:not(.disabled):not(.processing):not(.processing-legacy):not(.rejected) {
      border-color: #1cabe2;
    }
  }
  dt {
    width: ${({ compact }) => (compact ? '56px' : '62px')};
    font-size: ${({ compact }) => (compact ? '12px' : '14px')};
    color: ${({ checked }) => (checked ? '#fff' : '#828385')};
    opacity: ${({ checked }) => (checked ? '0.8' : '1')};
  }
  dd {
    font-size: ${({ compact }) => (compact ? '12px' : 'inherit')};
  }
  ${CardInfo} {
    font-size: ${({ compact }) => (compact ? '14px' : '16px')};
    gap: ${({ compact }) => (compact ? '8px' : '3px')};
  }
  ${CardInfo} > div {
    justify-content: ${({ Npay, compact }) => (Npay && !compact ? 'center' : 'flex-start')};
    ${breakpoint(640)} {
      font-size: ${({ Npay }) => (Npay ? '16px' : '14px')};
    }
  }
  ${breakpoint(640)} {
    min-height: ${({ compact }) => (compact ? 'auto' : '110px')};
  }
`;
const PaymentDay = styled.div<{ open: boolean }>`
  /* 화면 크기에 따라 자연스럽게 변하는 너비 */
  width: calc(100% + clamp(32px, 4vw, 64px));
  margin-left: calc(-1 * clamp(16px, 2vw, 32px));

  background: #f8f9fa;
  overflow: hidden;
  transition: height 0.35s ease;

  height: ${({ open }) => (open ? '116px' : '0')};
  padding: ${({ open }) => (open ? 'calc(24px) clamp(16px, 2vw, 32px)' : '0')};
  margin-top: ${({ open }) => (open ? '20px' : '0')};
  margin-bottom: ${({ open }) => (open ? '20px' : '0')};
  opacity: ${({ open }) => (open ? 1 : 0)};

  ${H4} {
    margin-bottom: 16px;
  }
`;
const RadioLabel = styled.label<{ compact?: boolean }>`
  display: block;
  margin-bottom: ${({ compact }) => (compact ? '12px' : '10px')};
  cursor: pointer;
`;
const AddButton = styled(Button)`
  width: 100%;
  font-size: 16px;
  color: #1cabe2;
  border: 1px dashed #1cabe2;
  border-radius: 10px;
  background: #fff;
  padding: 18px 0;
  ${breakpoint(640)} {
    font-size: 14px;
  }
`;
const SwiperCustom = styled.div`
  .swiper-container {
    padding: 0 40px;
    overflow-x: clip;
    overflow-y: visible;

    .swiper-slide {
      width: 247px !important;
      ${breakpoint(640)} {
        width: 100% !important;
      }
    }
  }
  /* swiper 내 툴팁 위치: 카드 중앙 정렬 */
  [data-tooltip-text] {
    left: 50%;
    right: auto;
    transform: translateX(-50%);
  }
  .swiper-button-prev,
  .swiper-button-next {
    display: block;
    width: 40px;
    height: 100%;
    margin-top: 0;
    top: 0;
    z-index: 10;
    cursor: pointer;
    &::after {
      content: '';
      display: block;
      width: 26px;
      height: 26px;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
    }
    &.swiper-button-disabled {
      opacity: 1;
      &::after {
        opacity: 0.5;
      }
    }
  }
  .swiper-button-prev {
    left: 0;
    background: linear-gradient(to right, #fff, #fff 80%, transparent 100%);
    &::after {
      left: 7px;
      background: url(${naviPrev}) no-repeat 0 0 / contain;
    }
  }
  .swiper-button-next {
    right: 0;
    background: linear-gradient(to left, #fff, #fff 80%, transparent 100%);
    &::after {
      right: 7px;
      background: url(${naviNext}) no-repeat 0 0 / contain;
    }
  }
  .radio-label {
    margin-bottom: 0;
  }
  ${AddButton} {
    height: 120px;
    margin-bottom: 0;
    ${breakpoint(640)} {
      height: 110px;
    }
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 116px;
  color: #828385;
`;

const ErrorWrapper = styled.div`
  padding: 20px;
  text-align: center;
  color: #e74c3c;
  background: #fef5f5;
  border-radius: 10px;
  margin-bottom: 20px;
`;

const SwiperTooltip = styled.div`
  margin: 12px 40px 0;
  padding: 12px 16px;
  border: 1px solid #1cabe2;
  background: #fff;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  text-align: left;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
    border-bottom: 9px solid #1cabe2;
  }
  &::after {
    content: '';
    position: absolute;
    bottom: calc(100% - 1px);
    left: 50%;
    transform: translateX(-50%);
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid #fff;
  }
`;

// InputHTMLAttributes를 extend하여 모든 input 속성 사용 가능
interface RadioButtonProps extends InputHTMLAttributes<HTMLInputElement> {
  Npay?: boolean; // 네이버페이
  children?: React.ReactNode;
  payType?: string;
  displayMode?: 'list' | 'swiper';
  isEnableOnly?: boolean;
  data?: MyPymentMethodVo;
  pledgeInfo?: any;
  isDefault?: boolean; // 현재 연결된 결제수단 여부
  showStatusBadge?: boolean; // 마이페이지용 상태 뱃지 표시
  onHoverTooltip?: (message: React.ReactNode) => void;
  onLeaveTooltip?: () => void;
}

type PaymentMethodListProps = {
  isMypage?: boolean;
  displayMode?: 'list' | 'swiper';
  isEnableOnly: boolean;
  showAddButton?: boolean;
  showApplyCheckbox?: boolean;
  showStatusBadge?: boolean; // 마이페이지용 상태 뱃지 및 compact UI
  onAddMethod?: () => void;
  onLoad?: (totalCount: number) => void;
  onChange?: (
    paymethodInfo: MyPymentMethodVo,
    isApplyAll?: boolean /* 결제수단 변경에서만 사용 */,
  ) => void;
  defaultSelected?: string;
  autoSelect?: boolean;
  moduleInfo?: any;
  pledgeInfo?: any;
  pledgeList?: any[]; // 정기후원 목록 (결제수단 정렬용 giftDate 포함)
};

const getPaymentMethodClassName = (
  isDisabled: boolean,
  status?: string,
  showStatusBadge?: boolean,
) => {
  if (!isDisabled) return '';

  if (status === 'New' || status === 'Processing') {
    return showStatusBadge ? 'processing' : 'processing-legacy';
  }

  if (status === 'Rejected') {
    return showStatusBadge ? 'rejected' : 'disabled';
  }

  return showStatusBadge ? 'processing' : 'disabled';
};

// 상태별 처리 정의:
// - Enrolled: 정상 활성화 (선택 가능)
// - New: 비활성화(옅은색), 툴팁(등록 진행중)
// - Processing: 비활성화(옅은색), 툴팁(등록 진행중)
// - Rejected: 비활성화(회색), 툴팁(등록 실패)
// - Processing Cancellation: 미노출
// - Canceled: 미노출
const enableStatusList = ['Enrolled']; // 활성화(선택 가능) 상태
const displayStatusList = ['New', 'Processing', 'Enrolled', 'Rejected']; // 노출 상태 (후원하기/마이페이지 공통)
const mypageDisplayStatusList = ['New', 'Processing', 'Enrolled', 'Rejected']; // 노출 상태 (마이페이지)

const getStatusBadge = (
  status?: string,
  isChecked?: boolean,
  isDefault?: boolean,
) => {
  if (isDefault && status === 'Enrolled') {
    return <StatusBadge variant={isChecked ? 'current' : 'currentUnselected'}>현재 사용 중</StatusBadge>;
  }
  if (status === 'Enrolled') {
    return <StatusBadge variant="available">사용가능</StatusBadge>;
  }
  if (status === 'New' || status === 'Processing') {
    return <StatusBadge variant="processing">등록 중</StatusBadge>;
  }
  if (status === 'Rejected') {
    return <StatusBadge variant="rejected">등록 실패</StatusBadge>;
  }
  return null;
};

const RadioButton = forwardRef<HTMLInputElement, RadioButtonProps>(
  (
    {
      children,
      Npay,
      payType,
      displayMode = 'list',
      isEnableOnly,
      data,
      isDefault,
      showStatusBadge,
      onHoverTooltip,
      onLeaveTooltip,
      ...props
    },
    ref,
  ) => {
    const statusList = enableStatusList;
    const isDisabled = !statusList.includes(data?.status || '');
    const isChecked = props.checked || false;
    const isProcessing = ['New', 'Processing'].includes(data?.status || '');
    const isRejected = data?.status === 'Rejected';
    const showTooltip = !showStatusBadge && (isProcessing || isRejected);
    const { type, disabled, ...restProps } = props;

    const content = (
      <RadioTabButton
        checked={isChecked}
        Npay={Npay || false}
        compact={showStatusBadge}
        className={getPaymentMethodClassName(
          isDisabled,
          data?.status,
          showStatusBadge,
        )}
      >
        <HiddenRadio
          disabled={isDisabled}
          {...restProps}
          ref={ref}
          type="radio"
        />
        {showStatusBadge && getStatusBadge(data?.status, isChecked, isDefault)}
        {children && (
          <CardInfo>
            {children}
            {showStatusBadge && isRejected && (
              <RejectedMessage>등록이 실패된 결제수단입니다.</RejectedMessage>
            )}
          </CardInfo>
        )}
      </RadioTabButton>
    );

    // 기존 툴팁 (후원하기 등 showStatusBadge가 아닌 경우)
    if (showTooltip) {
      const tooltipMessage = isProcessing ? (
        <span>
          현재 등록 진행 중이거나 확인이 필요한 결제수단입니다.
          <br />
          상담센터(02-737-1004)로 문의주세요.
        </span>
      ) : (
        <span>
          등록이 실패된 결제수단입니다.
          <br />
          다른 결제수단을 선택 또는 신규 결제수단을 추가해 주세요.
        </span>
      );

      if (displayMode === 'swiper') {
        return (
          <RadioLabel
            className="radio-label"
            onMouseEnter={() => onHoverTooltip?.(tooltipMessage)}
            onMouseLeave={() => onLeaveTooltip?.()}
            onClick={() => onHoverTooltip?.(tooltipMessage)}
          >
            {content}
          </RadioLabel>
        );
      }

      return (
        <RadioLabel className="radio-label">
          <Tooltip
            tip={tooltipMessage}
            size={350}
            position="bottom"
            css={`
              display: block;
              width: 100%;
            `}
          >
            {content}
          </Tooltip>
        </RadioLabel>
      );
    }

    return <RadioLabel className="radio-label" compact={showStatusBadge}>{content}</RadioLabel>;
  },
);

const statusSortOrder: Record<string, number> = {
  Enrolled: 0,
  New: 1,
  Processing: 1,
  Rejected: 2,
};

const sortPaymentMethods = (
  methods: MyPymentMethodVo[],
): MyPymentMethodVo[] => {
  return [...methods].sort((a, b) => {
    // Enrolled > New/Processing > Rejected
    return (statusSortOrder[a.status || ''] ?? 3) - (statusSortOrder[b.status || ''] ?? 3);
  });
};

const loadPaymentMethods = (
  moduleInfo: any,
  isEnableOnly: boolean,
  isMypage: boolean,
) => {
  const [paymentMethods, setPaymentMethods] = useState<MyPymentMethodVo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 결제수단 목록 조회
  const fetchPaymentMethods = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const {
        data,
      } = await MySupportInfoControllerService.getMyPaymentMethodListUsingPost({
        pathType: moduleInfo ? 'MISSMODULE' : 'SUPPORT',
      });
      if (data?.listPayment) {
        const statusList = isEnableOnly
          ? displayStatusList
          : mypageDisplayStatusList;

        const filtered = data?.listPayment.filter((method: any) =>
          statusList.includes(method.status || ''),
        );
        setPaymentMethods(sortPaymentMethods(filtered));
      } else {
        setPaymentMethods(data?.listPayment || []);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : '결제수단을 불러오는데 실패했습니다.',
      );
    } finally {
      setLoading(false);
    }
  }, [moduleInfo, isMypage]);

  // 초기 로드
  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  // 결제수단 추가
  const addPaymentMethod = useCallback(
    async (paymentData: Partial<PaymentMethod>) => {
      const success = await PaymentService.addPaymentMethod(paymentData);
      if (success) {
        await fetchPaymentMethods(); // 목록 새로고침
      }
      return success;
    },
    [fetchPaymentMethods],
  );

  return {
    paymentMethods,
    loading,
    error,
    refresh: fetchPaymentMethods,
    addPaymentMethod,
  };
};

const PaymentMethodList: React.FC<PaymentMethodListProps> = ({
  displayMode = 'list',
  isEnableOnly,
  showAddButton = true,
  showApplyCheckbox = true,
  showStatusBadge = false,
  onAddMethod,
  onLoad,
  onChange,
  defaultSelected,
  autoSelect = true,
  moduleInfo,
  pledgeInfo,
  pledgeList,
}) => {
  const { paymentMethods, loading, error, refresh } = loadPaymentMethods(
    moduleInfo,
    isEnableOnly,
    false, // isMypage
  );
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [payDay, setPayDay] = useState();
  const [isApplyAll, setIsApplyAll] = useState(false); // 결제수단 변경에서만 사용
  const [swiperTooltip, setSwiperTooltip] = useState<React.ReactNode | null>(
    null,
  );

  // 이벤트 생성 순서 중요!!!
  useEffect(() => {
    if (loading === false) {
      onLoad?.(paymentMethods.length);
    }
  }, [loading]);

  // 이벤트 생성 순서 중요!!!
  // 초기 기본 결제수단이 있으면 자동 선택
  useEffect(() => {
    if (loading) return;
    if (!autoSelect) return;

    if (!selected && paymentMethods.length > 0) {
      // 기본값이 있고 Enrolled 상태인 경우에만 사용
      const defaultMethod = defaultSelected
        ? paymentMethods.find(
            (method) =>
              method.mandateId === defaultSelected &&
              method.status === 'Enrolled', // Enrolled 상태만 허용
          )
        : null;

      // defaultSelected가 존재하지만 Enrolled 상태로 찾을 수 없는 경우
      // (등록 중, 목록에 없음 등) → 다른 수단을 자동 선택하지 않고 사용자가 직접 선택하도록 함
      const isDefaultNotSelectable = defaultSelected
        && !paymentMethods.some((m) => m.mandateId === defaultSelected && m.status === 'Enrolled');

      // Enrolled 상태만 자동 선택 (New, Processing은 선택 불가)
      const firstEnrolledMethod = isDefaultNotSelectable
        ? null
        : paymentMethods.find((method) => method.status === 'Enrolled');

      const firstMethod = defaultMethod || firstEnrolledMethod;

      // 선택 가능한 결제수단이 있을 때만 자동 선택
      if (firstMethod) {
        const pledgePayday = `${pledgeInfo?.payday || ''}`.padStart(2, '0');
        const paytype = getPaymentTypeNameToCode(firstMethod.paymentType || '');
        const validDays =
          PAYTYPE_PAYMENT_DAY_LIST[paytype]?.map((d: any) => d.day) || [];
        const initialDay = validDays.includes(pledgePayday)
          ? pledgePayday
          : '';
        setPayDay(initialDay);
        setSelected(firstMethod.mandateId);
        onChange?.({ ...firstMethod, paymentDay: initialDay }, isApplyAll);
      }
    }
  }, [
    paymentMethods,
    selected,
    onChange,
    isApplyAll,
    defaultSelected,
    autoSelect,
    loading,
  ]);

  const handleChange = (paymethodInfo: MyPymentMethodVo) => {
    setSelected(paymethodInfo.mandateId);
    onChange?.(paymethodInfo, isApplyAll);
  };

  const handleAddMethod = () => {
    setSelected('-');
    onAddMethod?.();
  };

  // 로딩 상태
  if (loading) {
    return <LoadingWrapper>결제수단을 불러오는 중...</LoadingWrapper>;
  }

  // 에러 상태
  if (error) {
    return (
      <>
        <ErrorWrapper>
          <p>{error}</p>
          <Button onClick={refresh} css="margin-top: 10px;">
            다시 시도
          </Button>
        </ErrorWrapper>
      </>
    );
  }

  // giftDate DESC 정렬 (pledgeList 기반, 상태 정렬은 loadPaymentMethods에서 처리됨)
  const giftDateMap = new Map<string, string>();
  if (pledgeList && pledgeList.length > 0) {
    for (const pledge of pledgeList) {
      const mid = pledge.mandateId;
      const gd = pledge.giftDate || '';
      if (mid && (!giftDateMap.has(mid) || gd > (giftDateMap.get(mid) || ''))) {
        giftDateMap.set(mid, gd);
      }
    }
  }
  const sortedPaymentMethods = [...paymentMethods].sort((a, b) => {
    const orderA = statusSortOrder[a.status || ''] ?? 3;
    const orderB = statusSortOrder[b.status || ''] ?? 3;

    // 상태 그룹 먼저 정렬 (Enrolled > New/Processing > Rejected)
    if (orderA !== orderB) return orderA - orderB;

    // 같은 상태 그룹 내에서 현재 연결된 결제수단 최상단
    if (defaultSelected) {
      const isDefaultA = a.mandateId === defaultSelected;
      const isDefaultB = b.mandateId === defaultSelected;
      if (isDefaultA !== isDefaultB) return isDefaultA ? -1 : 1;
    }

    // Enrolled 상태 간 giftDate DESC 정렬
    if (orderA === 0 && giftDateMap.size > 0) {
      const gdA = giftDateMap.get(a.mandateId) || '';
      const gdB = giftDateMap.get(b.mandateId) || '';
      if (gdA !== gdB) return gdB > gdA ? 1 : -1;
    }
    return 0;
  });

  // 네이버페이 순번 맵 생성 (복수 건일 때만 번호 부여)
  const npayMethods = sortedPaymentMethods.filter(
    (m) => m.paymentType === SALESFORCE_PAYMENT_METHOD_TYPE_NPAY,
  );
  const npayIndexMap = new Map<string, number>();
  if (npayMethods.length > 1) {
    const npayForIndex = [...npayMethods].sort((a, b) =>
      a.mandateId.localeCompare(b.mandateId),
    );
    npayForIndex.forEach((m, i) => {
      npayIndexMap.set(m.mandateId, i + 1);
    });
  }

  // 결제수단 목록이 비어있을 때
  const paymentMethodItems = sortedPaymentMethods.map((method: MyPymentMethodVo) => {
    const isNpay = method.paymentType === SALESFORCE_PAYMENT_METHOD_TYPE_NPAY;
    const showPaymentDay =
      selected === method.mandateId &&
      displayMode === 'list' &&
      method.status === 'Enrolled'; // Enrolled 상태만 결제일 표시
    return (
      <React.Fragment key={method.mandateId}>
        <RadioButton
          name="mandateId"
          value={method.mandateId}
          data={method}
          Npay={isNpay}
          isDefault={method.mandateId === defaultSelected}
          showStatusBadge={showStatusBadge}
          checked={selected === method.mandateId}
          onChange={() => {
            const pledgePayday = `${pledgeInfo?.payday || ''}`.padStart(2, '0');
            const paytype = getPaymentTypeNameToCode(method.paymentType || '');
            const validDays =
              PAYTYPE_PAYMENT_DAY_LIST[paytype]?.map((d: any) => d.day) || [];
            const initialDay = validDays.includes(pledgePayday)
              ? pledgePayday
              : '';
            setPayDay(initialDay);
            handleChange({
              ...method,
              paymentDay: initialDay,
            });
          }}
          payType={getPaymentTypeNameToCode(method.paymentType || '')}
          displayMode={displayMode}
          isEnableOnly={isEnableOnly}
          pledgeInfo={pledgeInfo}
          onHoverTooltip={
            displayMode === 'swiper' ? setSwiperTooltip : undefined
          }
          onLeaveTooltip={
            displayMode === 'swiper' ? () => setSwiperTooltip(null) : undefined
          }
        >
          <div>
            <strong>
              {getPaymentTypeLabel(method.paymentType || '')}
              {npayIndexMap.has(method.mandateId) &&
                ` ${npayIndexMap.get(method.mandateId)}`}
            </strong>
          </div>

          {/* 신용카드 정보 */}
          {method.paymentType === SALESFORCE_PAYMENT_METHOD_TYPE_CARD && (
            <>
              <div>
                <dt>카드사</dt>
                <dd>
                  <strong>{method.companyName}</strong>
                </dd>
              </div>
              <div>
                <dt>카드번호</dt>
                <dd>{fakeCardMask(method.paymentNumber || '')}</dd>
              </div>
            </>
          )}

          {/* 계좌이체 정보 */}
          {method.paymentType === SALESFORCE_PAYMENT_METHOD_TYPE_CMS && (
            <>
              <div>
                <dt>은행명</dt>
                <dd>
                  <strong>{method.companyName}</strong>
                </dd>
              </div>
              <div>
                <dt>계좌번호</dt>
                <dd>{maskNumber(method.paymentNumber || '')}</dd>
              </div>
            </>
          )}
        </RadioButton>
        {showPaymentDay && (
          <PaymentDay open>
            <H4>결제일</H4>
            <InputRadioPledgePaymentDay
              name="paymentDay"
              paytype={getPaymentTypeNameToCode(method.paymentType || '')}
              defaultDay={`${pledgeInfo?.payday || ''}`.padStart(2, '0') || ''}
              readOnly={false}
              onChange={(e) => {
                const payday = e.target.value;
                setPayDay(payday);
                handleChange({
                  ...method,
                  paymentDay: payday,
                });
              }}
            />
          </PaymentDay>
        )}
      </React.Fragment>
    );
  });

  return (
    <>
      {displayMode === 'swiper' ? (
        <SwiperCustom>
          <Swiper
            spaceBetween={10}
            slidesPerView="auto"
            navigation
            threshold={10}
            touchRatio={0.8}
            resistanceRatio={0.85}
            preventClicks={false}
            preventClicksPropagation={false}
            slideToClickedSlide={false}
          >
            {paymentMethodItems.map((item, index) => (
              <SwiperSlide key={`payment-method-${index}`}>{item}</SwiperSlide>
            ))}
            {showAddButton && (
              <SwiperSlide key="add-button">
                <AddButton
                  className="btn-add-method"
                  color="gray"
                  onClick={handleAddMethod}
                  css={`
                    margin-bottom: 40px;
                  `}
                >
                  + 결제수단 추가하기
                </AddButton>
              </SwiperSlide>
            )}
          </Swiper>
          {swiperTooltip && <SwiperTooltip>{swiperTooltip}</SwiperTooltip>}
        </SwiperCustom>
      ) : (
        <>
          <RadioWrapper>
            {sortedPaymentMethods.length > 0 ? (
              paymentMethodItems
            ) : (
              <LoadingWrapper>등록된 결제수단이 없습니다.</LoadingWrapper>
            )}
          </RadioWrapper>

          {showAddButton && (
            <AddButton
              className="btn-add-method"
              color="gray"
              onClick={handleAddMethod}
              css={`
                margin-bottom: 40px;
              `}
            >
              + 결제수단 추가하기
            </AddButton>
          )}

          {showApplyCheckbox && (
            <InputCheckbox
              label="다른 후원에도 적용"
              name="applyAllYn"
              value="Y"
              css={`
                margin-bottom: 16px;
              `}
              onClick={(item) => {
                const isChecked = item.currentTarget.checked;
                setIsApplyAll(isChecked);
                const selectPayMethod = paymentMethods.find(
                  (method) => method.mandateId === selected,
                );
                onChange?.(
                  { ...selectPayMethod, paymentDay: payDay },
                  isChecked,
                );
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default PaymentMethodList;
